import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the standard Convex React client package
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import
import { useInsuranceTypeList } from '@/hooks/useInsuranceTypeList';
import { useOrgContext } from '@/hooks/useOrgContext';
import { isValidProviderId } from '@/lib/type-guards';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ITypeListEntry } from '@/@types/insuranceBuilder';

function OrphanCard({
  type,
  selectedProviderId,
  onProviderChange,
  onAssign,
  isPending,
  providers,
}: {
  type: ITypeListEntry;
  selectedProviderId: Id<'insuranceProviders'> | undefined;
  onProviderChange: (typeId: string, providerId: Id<'insuranceProviders'>) => void;
  onAssign: (typeId: Id<'orgInsuranceTypes'>) => void;
  isPending: boolean;
  providers: Array<{ _id: Id<'insuranceProviders'>; name: string }>;
}) {
  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-foreground">{type.name}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {type.sectionCount} section{type.sectionCount !== 1 ? 's' : ''}
          {' · '}
          {type.fieldCount} field{type.fieldCount !== 1 ? 's' : ''}
          {' · '}
          {type.policyCount} polic{type.policyCount !== 1 ? 'ies' : 'y'}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Select
            value={selectedProviderId ?? ''}
            onValueChange={val => {
              if (isValidProviderId(val)) {
                onProviderChange(type._id, val);
              }
            }}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select a provider..." />
            </SelectTrigger>
            <SelectContent>
              {providers.map(p => (
                <SelectItem key={p._id} value={p._id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!selectedProviderId || isPending}
            onClick={() => onAssign(type._id)}
            className="shrink-0"
          >
            {isPending ? 'Saving...' : 'Assign'}
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/insurance/types/${type._id}`}
            className={cn('inline-flex items-center gap-1 text-sm text-primary hover:underline')}
          >
            Open in builder
            <ExternalLink className="w-3 h-3" aria-hidden="true" />
          </Link>
          <Button variant="ghost" size="sm" disabled className="text-muted-foreground text-sm">
            Split per policy (coming soon)
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function MigrationPage() {
  const { currentOrg } = useOrgContext();
  const { groups, loading } = useInsuranceTypeList();
  const reassignProvider = useMutation(api['insurance/types'].reassignProvider);

  const providers = useQuery(
    api['insurance/providers'].list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const [assignments, setAssignments] = useState<Record<string, Id<'insuranceProviders'>>>({});
  const [pending, setPending] = useState<string | null>(null);

  const orphanGroup = groups.find(g => g.providerId === null);
  const orphanTypes = orphanGroup?.types ?? [];

  function handleProviderChange(typeId: string, providerId: Id<'insuranceProviders'>) {
    setAssignments(prev => ({ ...prev, [typeId]: providerId }));
  }

  async function handleAssign(typeId: Id<'orgInsuranceTypes'>) {
    const providerId = assignments[typeId];
    if (!providerId) return;
    setPending(typeId);
    try {
      await reassignProvider({ typeId, providerId });
      toast.success('Provider assigned successfully');
      setAssignments(prev => {
        const next = { ...prev };
        delete next[typeId];
        return next;
      });
    } catch {
      toast.error('Failed to assign provider');
    } finally {
      setPending(null);
    }
  }

  if (loading || providers === undefined) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="max-w-3xl mx-auto space-y-4">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full rounded-md" />
          <Skeleton className="h-32 w-full rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2">
          <Link to="/insurance/types">
            <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />
            Back to Types
          </Link>
        </Button>

        <div className="flex items-start gap-3 mb-6">
          <AlertTriangle
            className="w-6 h-6 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5"
            aria-hidden="true"
          />
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Assign providers to migrated types
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              The following insurance types were migrated without a provider. Assign each one to a
              provider to complete the migration.
            </p>
          </div>
        </div>

        {orphanTypes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-500" aria-hidden="true" />
              <p className="text-base font-medium text-foreground">All types have been assigned</p>
              <p className="text-sm text-muted-foreground">
                Every insurance type is linked to a provider.
              </p>
              <Button variant="outline" asChild className="mt-2">
                <Link to="/insurance/types">Back to Types</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          orphanTypes.map(type => (
            <OrphanCard
              key={type._id}
              type={type}
              selectedProviderId={assignments[type._id]}
              onProviderChange={handleProviderChange}
              onAssign={handleAssign}
              isPending={pending === type._id}
              providers={providers ?? []}
            />
          ))
        )}
      </div>
    </div>
  );
}
